import { Link, useNavigate, useParams } from "react-router-dom";
import {
  RightMainContainer,
  HeaderContainer,
  PrimaryHeading,
  MainButton,
  BodyContainer,
  ListIconWrapper,
  ListCard,
  LoadingListCards,
  TemplateNotFound,
  InputField,
  FiltersList,
  UserAvatar,
} from "commonUI";
import { AddIcon } from "allIcons";
import { useMembers } from "api/usersModule/members";
import { useRoles } from "api/usersModule/roles";
import { isEqual, search } from "helpers";
import { USERTYPE } from "usersModule/enums";
import { useDeferredValue, useEffect, useRef, useState } from "react";
import {
  libraryItemFiltering,
  libraryItemSorting,
} from "filterAndSort/templateLibrary";
import { useUserLimitReached } from "hooks/plan";
import { useUserPermissions } from "hooks/user";
import { t } from "i18next";
import { Modules } from "helpers/constants";

export default function Members() {
  const { "*": path } = useParams();
  const modulePath = path.split("/")[0];
  const userTypeTranslation = isEqual(modulePath, "team-members")
    ? t("users.module.teamMember")
    : t("users.module.client");

  const navigate = useNavigate();
  const { data: members, isLoading: loadingMembers } = useMembers();
  const { data: roles, isLoading: loadingRoles } = useRoles();
  const { isLoading, isLimitReached } = useUserLimitReached();
  const {
    app_users_add_new_users,
    app_users_edit_users,
    app_users_view_users,
    isLoading: isPermissionsLoading,
  } = useUserPermissions();
  const pathRef = useRef(modulePath);

  const [searchText, setSearchText] = useState("");
  const [searchedData, setSearchedData] = useState([]);
  const defferedSearch = useDeferredValue(searchText);
  const [filterData, setFilterData] = useState({});

  useEffect(() => {
    if (isPermissionsLoading) return;
    if (!app_users_view_users) {
      navigate("/users/roles");
      return;
    }
  }, [isPermissionsLoading, app_users_view_users]);

  useEffect(() => {
    if (!isEqual(modulePath, pathRef.current)) {
      setFilterData({});
    }
  }, [modulePath]);

  useEffect(() => {
    setSearchedData(
      members.filter((_) => {
        const isUserRelated =
          isEqual(USERTYPE[modulePath], _.user_type) &&
          search(`${_.name} ${_.last_name}`, defferedSearch);
        if (!isEqual(_.is_owner, 1)) {
          return isUserRelated;
        }
        return modulePath === "team-members" && isEqual(_.is_owner, 1);
      })
    );
  }, [defferedSearch, members, modulePath]);

  useEffect(() => {
    setSearchText("");
  }, [modulePath]);

  const applySorting = (oder, orderBy) => {
    setSearchedData(libraryItemSorting(oder, orderBy, searchedData, "name"));
  };

  const applyFilter = ({
    selectedFilter,
    selectedTags,
    selectedLibrarySource,
    ...data
  }) => {
    setFilterData({
      ...data,
      selectedTags,
      selectedLibrarySource,
      selectedFilter,
    });
    setSearchedData(
      libraryItemFiltering(
        selectedFilter,
        selectedTags,
        selectedLibrarySource,
        members.filter(
          (_) =>
            (isEqual(USERTYPE[modulePath], _.user_type) ||
              (modulePath === "team-members" && isEqual(_.is_owner, 1))) &&
            search(`${_.name} ${_.last_name}`, defferedSearch)
        ),
        "user_role"
      )
    );
  };

  return (
    <>
      <RightMainContainer>
        <HeaderContainer>
          <PrimaryHeading>
            {isEqual(modulePath, "team-members")
              ? t("users.teamMembers.title")
              : t("users.clients.title")}
          </PrimaryHeading>
          {isLoading || isLimitReached || !app_users_add_new_users ? (
            ""
          ) : (
            <Link to={"/users/" + modulePath + "/add"}>
              <MainButton>
                <AddIcon />
                {t("button.add")}
              </MainButton>
            </Link>
          )}
        </HeaderContainer>
        <BodyContainer>
          <InputField
            placeholder={t("input.search.placeholder")}
            searchIcon
            filter
            value={searchText}
            setValue={(value) => setSearchText(value)}
            applySorting={applySorting}
            showTags={false}
            filterList={
              roles
                ?.filter((_) => isEqual(_.user_role, USERTYPE[modulePath]))
                .map((_) => ({
                  id: _.id,
                  title: _.role_title,
                  user_role: _.id,
                  module: Modules.ROLES,
                })) ?? []
            }
            filterData={filterData}
            filterTitle={t("filter.userRole.title")}
            filterAddTitle={t("filter.userRole.addRole")}
            sortOptions={[
              {
                label: t("sort.options.alphabeticalByTitle"),
                value: "alphabetical-by-title",
              },
              {
                label: t("sort.options.dateAdded"),
                value: "date-added",
              },
            ]}
            onApplyFilter={applyFilter}
          />
          <FiltersList filterData={filterData} updateFilter={applyFilter} />
          {loadingMembers || loadingRoles ? (
            <LoadingListCards />
          ) : searchedData.length === 0 ? (
            <TemplateNotFound
              label={userTypeTranslation}
              linkLabel={userTypeTranslation}
              link={
                isLimitReached || !app_users_add_new_users
                  ? ""
                  : "/users/" + modulePath + "/add"
              }
            />
          ) : (
            searchedData.map((item, index) => {
              return (
                <ListCard
                  key={index}
                  data={`${item.name} ${item.last_name}`}
                  link={
                    app_users_edit_users
                      ? `/users/${modulePath}/${item.id}`
                      : ""
                  }
                  secondaryData={
                    roles.find((_) => isEqual(_.id, item.user_role))
                      ?.role_title ?? ""
                  }
                  strictLink
                  editIcon={app_users_edit_users}
                  deleteIcon={true}
                  icon={
                    <ListIconWrapper className="bg-white">
                      <UserAvatar user={item} />
                    </ListIconWrapper>
                  }
                />
              );
            })
          )}
        </BodyContainer>
      </RightMainContainer>
    </>
  );
}
